/**
 * Schlägt ein eindeutiges Kürzel (shortName) aus Vor- und Nachname vor.
 *
 * Basis: die ersten zwei Zeichen des Nachnamens + die ersten zwei Zeichen des
 * Vornamens (z. B. "Mustermann", "Max" → "MuMa").
 *
 * Ist die Basis bereits vergeben, wird zuerst der Vornamen-Teil, dann der
 * Nachnamen-Teil zeichenweise verlängert und zuletzt durchnummeriert. Die
 * Kollisionsprüfung läuft über ein Set (O(1) statt linearer Suche pro Kandidat).
 * Groß- und Kleinschreibung zählen: "MD" und "Md" sind verschiedene Kürzel
 * (Schulen mit vielen Kolleginnen nutzen das bewusst).
 *
 * Gibt einen leeren String zurück, solange Vor- oder Nachname fehlt.
 */
export function suggestShortName(firstName: string, lastName: string, takenShortNames: Iterable<string>): string {
  const fn = firstName.trim();
  const ln = lastName.trim();
  if (!fn || !ln) return '';

  const taken = new Set<string>();
  for (const name of takenShortNames) {
    if (name) taken.add(name.trim());
  }

  const isFree = (candidate: string) => !taken.has(candidate);

  const base = ln.slice(0, 2) + fn.slice(0, 2);
  if (isFree(base)) return base;

  // Kollision: Vornamen-Teil verlängern …
  for (let k = 3; k <= fn.length; k++) {
    const candidate = ln.slice(0, 2) + fn.slice(0, k);
    if (isFree(candidate)) return candidate;
  }
  // … dann Nachnamen-Teil verlängern.
  for (let k = 3; k <= ln.length; k++) {
    const candidate = ln.slice(0, k) + fn.slice(0, 2);
    if (isFree(candidate)) return candidate;
  }

  // Fallback: durchnummerieren, bis ein freies Kürzel gefunden ist.
  let suffix = 2;
  while (!isFree(base + suffix)) suffix++;
  return base + suffix;
}

/**
 * Prüft, ob ein Kürzel bereits in der Liste der vergebenen Kürzel enthalten ist
 * (Groß- und Kleinschreibung zählen, getrimmt). Leeres Kürzel gilt als "nicht vergeben".
 */
export function isShortNameTaken(shortName: string, takenShortNames: Iterable<string>): boolean {
  const value = shortName.trim();
  if (!value) return false;
  for (const name of takenShortNames) {
    if (name && name.trim() === value) return true;
  }
  return false;
}
